<template>
  <b-card no-body class="card-container" v-if="ready">
    <!-- HEADER -->
    <template #header>
      <b-row class="mb-2 mt-2">
        <!-- Colonne gauche : champ de recherche -->
        <b-col md="6">
          <b-input-group class="search-widget simple-search">
            <template #prepend>
              <b-input-group-text>
                <font-awesome-icon icon="search" />
              </b-input-group-text>
            </template>
            <b-form-input
              type="search"
              :placeholder="$t('search_attribute')"
              v-model="searchQuery"
            />
          </b-input-group>
        </b-col>

        <!-- Colonne droite : bouton Create -->
        <b-col md="6" class="text-right">
          <b-button
            variant="primary"
            :href="createAttributeRoute()"
            class="btn-round"
          >
            <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" />
            {{ $t("Create a new attribute") }}
          </b-button>
        </b-col>
      </b-row>
    </template>

    <!-- BODY -->
    <b-card-body>
      <!-- Tableau principal -->
      <b-table
        striped
        hover
        responsive="sm"
        class="mb-3"
        id="attributes-list"
        ref="attributesList"
        :fields="fields"
        :items="getItems"
        :current-page="pagination.currentPage"
        :busy="loading"
        show-empty
        :empty-text="$t('No attributes found')"
      >
        <!-- Table vide -->
        <template #empty="scope">
          <div class="lead text-center py-3">
            {{ scope.emptyText }}
          </div>
        </template>

        <!-- Overlay de chargement -->
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle" />
            <strong>{{ $t("loading") }}</strong>
          </div>
        </template>

        <!-- Colonne : options -->
        <template #cell(options)="data">
          {{ data.item.options ? data.item.options.length : 0 }}
        </template>

        <!-- Colonne : created_at -->
        <template #cell(created_at)="data">
          {{ formatDate(data.item.created_at) }}
        </template>

        <!-- Colonne : updated_at -->
        <template #cell(updated_at)="data">
          {{ formatDate(data.item.updated_at) }}
        </template>

        <!-- Colonne : actions -->
        <template #cell(actions)="data">
          <b-button
            size="sm"
            variant="outline-primary"
            :href="editRoute(data.item.id)"
          >
            <font-awesome-icon :icon="['fas', 'pen']" />
            {{ $t("Edit") }}
          </b-button>

          <delete-button
            @success="deleteItem(data.item.id)"
            class="ml-2 actions-danger"
            type="product attribute"
            :title="`Delete attribute ${data.item.name}`"
          />
        </template>
      </b-table>

      <!-- Pagination -->
      <b-pagination
        v-if="pagination.last_page > 1"
        v-model="pagination.currentPage"
        :total-rows="pagination.total"
        :per-page="pagination.per_page"
        @change="reload"
        aria-controls="attributes-list"
      ></b-pagination>
    </b-card-body>

    <!-- FOOTER -->
    <b-card-footer>
      <div class="text-center" v-if="pagination.total > 0">
        {{
          $t("showing_x_results", {
            current: pagination.to - pagination.from + 1,
            total: pagination.total,
          })
        }}
      </div>
    </b-card-footer>
  </b-card>
</template>

<script>
import axios from "axios";
import moment from "moment";
import deleteButton from "../../Common/DeleteButton";

export default {
  name: "ProductAttributeList",
  components: { deleteButton },
  props: {
    initialAttributes: {
      type: Array,
      required: true
    },
    initialPagination: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      // Champs de la table
      fields: [
        { key: "name", label: this.$t("Name"), sortable: true },
        { key: "options", label: this.$t("Options"), sortable: false },
        { key: "created_at", label: this.$t("Created"), sortable: true },
        { key: "updated_at", label: this.$t("Updated"), sortable: true },
        { key: "actions", label: "", class: "text-right" }
      ],

      // Données initiales
      attributes: this.initialAttributes,

      // Pagination
      pagination: {
        total: this.initialPagination.total || 0,
        per_page: this.initialPagination.per_page || 15,
        currentPage: this.initialPagination.current_page || 1,
        last_page: this.initialPagination.last_page || 1,
        from: this.initialPagination.from || 1,
        to: this.initialPagination.to || 1
      },

      // Recherche
      searchQuery: "",
      loading: false,
      ready: false
    };
  },
  mounted() {
    this.ready = true;
  },
  methods: {
    // Méthode provider <b-table>
    getItems(ctx, cb) {
      this.pagination.currentPage = ctx.currentPage;
      this.fetchAttributes().then(() => {
        cb(this.attributes);
      });
      return null;
    },

    // Reload : on émet l’event pour recharger
    reload() {
      this.$root.$emit("bv::refresh::table", "attributes-list");
    },

    formatDate(date) {
      if (!date) return "";
      return moment(date).format("DD-MM-YYYY HH:mm");
    },

    createAttributeRoute() {
      return "/settings/product-attributes/new";
    },
    editRoute(id) {
      return `/settings/product-attributes/${id}/edit`;
    },

    deleteItem(id) {
      axios
        .delete(`/api/product-attributes/${id}`)
        .then(() => {
          this.$bvToast.toast(this.$t("Attribute deleted successfully"), {
            title: this.$t("Success"),
            variant: "success",
            solid: true
          });
          this.reload();
        })
        .catch(error => {
          this.$bvToast.toast(this.$t("Failed to delete attribute"), {
            title: this.$t("Error"),
            variant: "danger",
            solid: true
          });
        });
    },

    fetchAttributes() {
      this.loading = true;
      const params = {
        page: this.pagination.currentPage,
        query: this.searchQuery,
        full_opt: true
      };
      return axios
        .get(`/api/product-attributes`, { params })
        .then(response => {
          this.attributes = response.data.data;
          const meta = response.data.meta || {};
          this.pagination.total = meta.total || 0;
          this.pagination.per_page = meta.per_page || 15;
          this.pagination.currentPage = meta.current_page || 1;
          this.pagination.last_page = meta.last_page || 1;
          this.pagination.from = meta.from || 1;
          this.pagination.to = meta.to || 1;
        })
        .finally(() => {
          this.loading = false;
        });
    }
  },
  watch: {
    // Surveiller la recherche, relancer la page 1 et reload la table
    searchQuery: {
      handler() {
        this.pagination.currentPage = 1;
        this.reload();
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.card-container {
  margin-bottom: 1rem;
}
</style>
